import {
  Category,
  CONTACT_WAY_NAME_MAX_LENGTH,
  Criterion,
  ICriterion,
  ICriterionData
} from '@/entities'
import { TravelFormat } from '@/entities/abstract/travelFormat.model'

export interface IOption<T> {
  name: string
  value?: T
}

export type Range = [number, number]


export interface ICheckboxOptionData extends IOption<boolean> {
  id?: number
  description?: string
}
export interface ICheckboxOption extends ICheckboxOptionData {
  id: number
  key: string

  validate(): boolean
  update(data?: ICheckboxOptionData): void
}
export class CheckboxOption implements ICheckboxOption {
  id: number
  name: string
  value: boolean
  description?: string

  constructor(data: ICheckboxOptionData) {
    this.id = data.id || 0
    this.name = data.name
    this.description = data.description
    this.value = data.value || false
  }

  get key(): string {
    let id = this.id;
    return (id ? (id + '-') : '') + this.name;
  }

  validate(): boolean {
    return this.validateId() && this.validateName()
  }

  validateId(): boolean {
    let id = this.id
    return !!id && id > 0
  }

  validateName(): boolean {
    let name = this.name
    return !!name && name.length <= CONTACT_WAY_NAME_MAX_LENGTH
  }

  update(data?: ICheckboxOptionData) {
    this.name = data?.name || this.name
    this.description = data?.description || this.description
  }
}

export interface IContactWayOptionData extends ICheckboxOptionData {
  type?: string
}
export interface IContactWayOption extends IContactWayOptionData {
  key: string

  validate(): boolean
  update(data?: IContactWayOptionData): void
}
export class ContactWayOption extends CheckboxOption implements IContactWayOption {
  type?: string

  constructor(data: IContactWayOptionData) {
    super(data);
    this.type = data.type
  }
}


export type CriterionType = 'check' | 'range';

export type FilterNameType = string | Category | TravelFormat;

export interface IFilterData {
  readonly label: string;
  readonly type?: CriterionType;
  readonly min?: number;
  readonly max?: number;
  range?: Range;
  items?: ICriterionData<FilterNameType>[];
  selected?: boolean;
}

export interface IFilter extends IFilterData {
  readonly min: number;
  readonly max: number;
  range: Range;
  reset(): void;

  _items?: ICriterion<FilterNameType>[];
  items?: ICriterion<FilterNameType>[];  // get/set
  selected: boolean;
  selectedFilters: ICriterion<FilterNameType>[];
  selectedFilterNames: string[];
  selectedFilterIds: number[];
  filterData: any[];
}

export class Filter implements IFilter {
  readonly label: string
  readonly type: CriterionType
  readonly min: number
  readonly max: number
  _items?: ICriterion<FilterNameType>[]
  range: Range

  constructor(data: IFilterData) {
    this.label = data.label
    this.type = data.type || 'check'

    let min = data.min || 1
    let max = data.max || 100
    if (min > max) {
      [min, max] = [max, min]
    }
    this.min = min
    this.max = max

    let range = data.range
    min = range ? (range[0] || min) : min
    max = range ? (range[1] || max) : max
    if (min > max) {
      [min, max] = [max, min]
    }
    this.range = [
      min < this.min ? this.min : min,
      max > this.max ? this.max : max
    ]

    this._items = data.items?.map((item: ICriterionData<FilterNameType>) => new Criterion(item))
  }

  get items(): ICriterion<FilterNameType>[] {
    return this._items || []
  }
  set items(options: ICriterion<FilterNameType>[]) {
    options.forEach((option: ICriterion<FilterNameType>) => {
      let matchedItem = this.items?.find((item: ICriterion<FilterNameType>) => item.key === option.key)
      if (matchedItem) {
        Object.assign(matchedItem, option);
      }
    })
  }

  get selected(): boolean {
    if (this.type === 'range') {
      return !(this.range[0] === this.min && this.range[1] === this.max);
    } else {
      return !!this.items?.reduce((res: boolean, item: ICriterion<FilterNameType>) => {
        return res || !!item.value
      }, false);
    }
  }

  get selectedFilters(): ICriterion<FilterNameType>[] {
    return this.items?.filter((item: ICriterion<FilterNameType>) => !!item.value);
  }

  get selectedFilterNames(): string[] {
    return this.selectedFilters?.map((item: ICriterion<FilterNameType>) => item.name as string).filter(Filter.unique);
  }

  get selectedFilterIds(): number[] {
    let validFilters = this.selectedFilters?.filter((item: ICriterion<FilterNameType>) => !!item.id);
    return validFilters?.map((item: ICriterion<FilterNameType>) => item.id || 0).filter(Filter.unique);
  }

  private static unique(value: any, index: number, self: any[]): boolean {
    return self.indexOf(value) === index;
  }

  reset(): void {
    if (this.type === 'range') {
      this.range = [this.min, this.max];
    } else {
      this.items.forEach((item: ICriterion<FilterNameType>) => {
        item.value = false;
      });
    }
  }

  get filterData(): any[] {
    return [];
  }
}



export type SortDirection = 'direct' | 'reverse'

export interface ISortData {
  field?: string
  direction?: SortDirection
}

export interface ISort extends ISortData {
  field: string
  direction: SortDirection
}

export class Sort implements ISort {
  field: string
  direction: SortDirection

  constructor(data?: ISortData) {
    this.field = data?.field || 'name'
    this.direction = data?.direction || 'reverse'
  }

}


export interface ILink {
  label: string
  ref: string
}

export type I18nPayload = string | number;

export interface IValidationObject {
  readonly slug?: string
  readonly payload?: I18nPayload[]
  readonly message?: string
}

export type IValidation = IValidationObject | null;

export type InputElement = HTMLInputElement | HTMLTextAreaElement;

export const AvailableIconsArray = [
  'chevron',
  'email',
  'star',
  'check',
  'close',
  'warning',
  'info',
  'config',
  'message',
  'open',
  'download',

  'case',
  'season',
  'wallet',
  'location',
  'person',
  'accommodation',
  'travel_format',
  'price',
  'time',
  'calendar',
  'taste',
  'quotes',
  'menu',
  'history',

  // sort
  'direct',
  'reverse',

  // social
  'facebook',
  'facebook messenger',
  'telegram',
  'viber',
  'whatsapp',
  'instagram'
] as const;
export type AvailableIcons = typeof AvailableIconsArray[number];

export type FormStatus = 'success' | 'danger' | ''

export type StatusTagView = '' | 'secondary' | 'tertiary' | 'awesome'
export type StatusTagSize = '' | 'sm'

export const StatusTagTypeArray = [
  'danger',
  'success',
  'warning',
  'progress',
  'none'
] as const;
export type StatusTagType = typeof StatusTagTypeArray[number];

export type JustifyDirection = 'left' | 'center' | 'right'
